export type ProtectedIdentityField =
  | "email"
  | "phone"
  | "externalId"
  | "fullName"
  | "company"
  | "jobTitle"
  | "website"
  | "first"
  | "middle"
  | "last"
  | "street"
  | "city"
  | "state"
  | "postalCode"
  | "country";

const FIELD_ALIASES: Record<ProtectedIdentityField, readonly string[]> = {
  email: [
    "email",
    "emailaddress",
    "workemail",
    "businessemail",
    "useremail",
    "customeremail",
    "contactemail",
    "billingemail",
    "youremail",
  ],
  phone: [
    "phone",
    "phonenumber",
    "telephone",
    "tel",
    "mobile",
    "mobilephone",
    "userphone",
    "customerphone",
    "contactphone",
    "billingphone",
    "yourphone",
  ],
  externalId: ["externalid", "userid", "customerid", "accountid", "contactid"],
  fullName: ["name", "fullname", "yourname", "contactname", "customername"],
  company: [
    "company",
    "companyname",
    "organization",
    "organisation",
    "businessname",
  ],
  jobTitle: ["jobtitle", "position", "occupation", "organizationtitle"],
  website: [
    "website",
    "websiteurl",
    "companywebsite",
    "companyurl",
    "businesswebsite",
  ],
  first: [
    "first",
    "firstname",
    "givenname",
    "customerfirstname",
    "contactfirstname",
    "billingfirstname",
  ],
  middle: ["middle", "middlename", "additionalname"],
  last: [
    "last",
    "lastname",
    "surname",
    "familyname",
    "customerlastname",
    "contactlastname",
    "billinglastname",
  ],
  street: [
    "street",
    "streetaddress",
    "address",
    "address1",
    "addressline1",
    "billingaddress1",
  ],
  city: ["city", "locality", "town", "billingcity"],
  state: ["state", "province", "region", "addresslevel1", "billingstate"],
  postalCode: ["postalcode", "postcode", "zipcode", "zip", "billingpostcode"],
  country: ["country", "countrycode", "countryname", "billingcountry"],
};

const AUTOCOMPLETE_TOKENS: Partial<
  Record<ProtectedIdentityField, readonly string[]>
> = {
  email: ["email"],
  phone: ["tel"],
  fullName: ["name"],
  company: ["organization"],
  jobTitle: ["organization-title"],
  website: ["url"],
  first: ["given-name"],
  middle: ["additional-name"],
  last: ["family-name"],
  street: ["street-address", "address-line1"],
  city: ["address-level2"],
  state: ["address-level1"],
  postalCode: ["postal-code"],
  country: ["country", "country-name"],
};

const ALIAS_TO_FIELD = new Map<string, ProtectedIdentityField>();
for (const [field, aliases] of Object.entries(FIELD_ALIASES) as Array<
  [ProtectedIdentityField, readonly string[]]
>) {
  for (const alias of aliases) ALIAS_TO_FIELD.set(alias, field);
}

export function normalizeIdentityFieldKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function identifyProtectedField(
  value: unknown,
): ProtectedIdentityField | undefined {
  if (typeof value !== "string") return undefined;
  return ALIAS_TO_FIELD.get(normalizeIdentityFieldKey(value));
}

export function isProtectedIdentityTraitKey(value: string) {
  return identifyProtectedField(value) !== undefined;
}

function readString(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

export function readIdentityRecordValue(
  record: Record<string, unknown>,
  field: ProtectedIdentityField,
) {
  const values = new Map<string, string>();
  for (const [key, rawValue] of Object.entries(record)) {
    const value = readString(rawValue);
    if (!value) continue;
    const normalizedKey = normalizeIdentityFieldKey(key);
    if (!values.has(normalizedKey)) values.set(normalizedKey, value);
  }
  for (const alias of FIELD_ALIASES[field]) {
    const value = values.get(alias);
    if (value) return value;
  }
  return undefined;
}

export type IdentityFormControl = {
  name?: unknown;
  id?: unknown;
  type?: unknown;
  autocomplete?: unknown;
  placeholder?: unknown;
  disabled?: unknown;
  checked?: unknown;
  value?: unknown;
  labels?: ArrayLike<{ textContent?: unknown }> | null;
  getAttribute?: (name: string) => string | null;
};

export type IdentityForm = {
  elements: ArrayLike<unknown>;
};

export type SensitiveFormFieldKind =
  | "password"
  | "payment"
  | "medical"
  | "security_token"
  | "government_id"
  | "file";

export type IdentityFormInspection = {
  identityFields: ProtectedIdentityField[];
  sensitiveFields: SensitiveFormFieldKind[];
  unsupportedIdentityHint: boolean;
  fieldCount: number;
};

function readControlAttribute(control: IdentityFormControl, name: string) {
  try {
    return typeof control.getAttribute === "function"
      ? control.getAttribute(name)
      : null;
  } catch {
    return null;
  }
}

function controlDescriptorValues(control: IdentityFormControl) {
  const values = [
    readString(control.name),
    readString(control.id),
    readString(control.placeholder),
    readString(readControlAttribute(control, "aria-label")),
  ];
  try {
    if (control.labels) {
      for (const label of Array.from(control.labels)) {
        values.push(readString(label?.textContent));
      }
    }
  } catch {
    // Host controls can expose throwing accessors. Treat them as unsupported.
  }
  return values.filter((value): value is string => Boolean(value));
}

function descriptorKey(control: IdentityFormControl) {
  return controlDescriptorValues(control)
    .map(normalizeIdentityFieldKey)
    .filter(Boolean)
    .join(" ");
}

export function classifySensitiveFormControl(
  control: IdentityFormControl,
): SensitiveFormFieldKind | undefined {
  const type = readString(control.type)?.toLowerCase();
  const autocomplete = (
    readString(control.autocomplete) ??
    readString(readControlAttribute(control, "autocomplete")) ??
    ""
  ).toLowerCase();
  const descriptor = descriptorKey(control);

  if (
    type === "password" ||
    /(?:^|\s)(?:new-|current-)?password(?:\s|$)/.test(autocomplete)
  ) {
    return "password";
  }
  if (type === "file") return "file";
  if (
    /(?:^|\s)(?:cc-|transaction-)/.test(autocomplete) ||
    /(creditcard|cardnumber|ccnumber|cardsecurity|cvv|cvc|expiry|expiration|iban|routingnumber|bankaccount|paymenttoken)/.test(
      descriptor,
    )
  ) {
    return "payment";
  }
  if (
    /(medical|health|diagnos|symptom|medication|prescription|patient|disease|clinical|insurancepolicy)/.test(
      descriptor,
    )
  ) {
    return "medical";
  }
  if (
    /(?:^|\s)one-time-code(?:\s|$)/.test(autocomplete) ||
    /(?:^|\s)[a-z0-9]*token(?:\s|$)/.test(descriptor) ||
    /(csrf|xsrf|apikey|clientsecret|otp|onetimecode|verificationcode|passcode|privatekey)/.test(
      descriptor,
    )
  ) {
    return "security_token";
  }
  if (
    /(?:^|\s)sin(?:\s|$)/.test(descriptor) ||
    /(socialsecurity|socialinsurancenumber|ssn|sinnumber|passport(?:number|id)?|drivers?licen[sc]e(?:number)?|nationalid|governmentid|taxid)/.test(
      descriptor,
    )
  ) {
    return "government_id";
  }
  return undefined;
}

function controlValue(control: IdentityFormControl) {
  if (control.disabled === true) return undefined;
  if (classifySensitiveFormControl(control)) return undefined;
  const type = readString(control.type)?.toLowerCase();
  if (
    type === "button" ||
    type === "submit" ||
    type === "reset" ||
    type === "image"
  ) {
    return undefined;
  }
  if ((type === "checkbox" || type === "radio") && control.checked !== true) {
    return undefined;
  }
  return readString(control.value);
}

function explicitMappedField(control: IdentityFormControl) {
  return identifyProtectedField(
    readControlAttribute(control, "data-prospix-identity-field"),
  );
}

function autocompleteMatches(
  control: IdentityFormControl,
  field: ProtectedIdentityField,
) {
  const autocomplete =
    readString(control.autocomplete) ??
    readString(readControlAttribute(control, "autocomplete"));
  if (!autocomplete) return false;
  const tokens = new Set(autocomplete.toLowerCase().split(/\s+/));
  return (
    AUTOCOMPLETE_TOKENS[field]?.some((token) => tokens.has(token)) === true
  );
}

function nameOrIdMatches(
  control: IdentityFormControl,
  field: ProtectedIdentityField,
) {
  return [control.name, control.id].some(
    (value) => identifyProtectedField(value) === field,
  );
}

function typeMatches(
  control: IdentityFormControl,
  field: ProtectedIdentityField,
) {
  const type = readString(control.type)?.toLowerCase();
  return (
    (field === "email" && type === "email") ||
    (field === "phone" && type === "tel")
  );
}

function descriptorMatches(
  control: IdentityFormControl,
  field: ProtectedIdentityField,
) {
  return controlDescriptorValues(control).some((value) => {
    if (identifyProtectedField(value) === field) return true;
    const normalized = normalizeIdentityFieldKey(value);
    if (field === "email") {
      return /(?:email|emailaddress)$/.test(normalized);
    }
    if (field === "phone") {
      return /(?:phone|phonenumber|telephone|mobile|mobilephone)$/.test(
        normalized,
      );
    }
    return false;
  });
}

function resolveFormControl(control: IdentityFormControl) {
  if (classifySensitiveFormControl(control)) return undefined;
  const explicit = explicitMappedField(control);
  if (explicit) return { field: explicit, priority: 0 };
  const fields = Object.keys(FIELD_ALIASES) as ProtectedIdentityField[];
  const matchers = [
    autocompleteMatches,
    nameOrIdMatches,
    descriptorMatches,
    typeMatches,
  ];
  for (const [index, matches] of matchers.entries()) {
    const field = fields.find((candidate) => matches(control, candidate));
    if (field) return { field, priority: index + 1 };
  }
  return undefined;
}

export function identifyFormControlField(
  control: IdentityFormControl,
): ProtectedIdentityField | undefined {
  return resolveFormControl(control)?.field;
}

export function inspectIdentityForm(
  form: IdentityForm,
): IdentityFormInspection {
  const controls = Array.from(form.elements).filter(
    (entry): entry is IdentityFormControl =>
      Boolean(entry && typeof entry === "object"),
  );
  const identityFields = new Set<ProtectedIdentityField>();
  const sensitiveFields = new Set<SensitiveFormFieldKind>();
  let unsupportedIdentityHint = false;

  for (const control of controls) {
    const sensitive = classifySensitiveFormControl(control);
    if (sensitive) {
      sensitiveFields.add(sensitive);
      continue;
    }
    const field = identifyFormControlField(control);
    if (field) identityFields.add(field);
    const explicit = readString(
      readControlAttribute(control, "data-prospix-identity-field"),
    );
    if (explicit && !identifyProtectedField(explicit)) {
      unsupportedIdentityHint = true;
    }
  }

  return {
    identityFields: Array.from(identityFields).sort(),
    sensitiveFields: Array.from(sensitiveFields).sort(),
    unsupportedIdentityHint,
    fieldCount: controls.length,
  };
}

export function readIdentityFormValue(
  form: IdentityForm,
  field: ProtectedIdentityField,
) {
  const controls = Array.from(form.elements).filter(
    (entry): entry is IdentityFormControl =>
      Boolean(entry && typeof entry === "object"),
  );
  const candidates = controls.flatMap((control) => {
    const resolved = resolveFormControl(control);
    return resolved?.field === field ? [{ control, ...resolved }] : [];
  });
  candidates.sort((a, b) => a.priority - b.priority);
  for (const { control } of candidates) {
    const value = controlValue(control);
    if (value) return value;
  }
  return undefined;
}
